import { BaggageInfo, AGBaggageInfo, ExtendedPrice } from "./BookingResponse";
import { Traveler } from "./FlightBookingForm";
import { DealDiscount } from "./Flights";
import { PNR_STATUSES } from "../../enums/FLIGHT_ISSUANCE";
import { PassengerFieldTypes } from "@/modules/Booking/constants";
import { PhoneFieldValue } from "@/core/interfaces/profile.interface";
import { IPassenger } from "@/core/interfaces/passenger.interface";
import { OptionValue } from "./utils";

export * from "./AgentBalance";
export * from "./AgentDetailsLimitRequest";
export * from "./AgentLimitRequest";
export * from "./AgentUser";
export * from "./Airline";
export * from "./Airport";
export * from "./BookingListItem";
export * from "./BookingResponse";
export * from "./Flights";
export * from "./JourneyLeg";
export * from "./Leg";
export * from "./Organization";
export * from "./OrganizationCategory";
export * from "./OtpType";
export * from "./Place";
export * from "./RequestHistory";
export * from "./Sector";
export * from "./Segment";
export * from "./User";
export * from "./DestinationDisplay";
export * from "./BookingData";
export * from "./BookingForm";

export type Settings = {
  sabre_airlines: Array<SabreAirlineConfig>;
  airline: Array<SettingsAirline>;
  providers: Array<SettingsAirline>;
  cc_payment_mdr_charge: number;
  one_bill_payment_flat_charge: number;
};

export type SabreAirlineConfig = {
  pcc: string;
  airline_code: string;
};

export type SettingsAirline = {
  id: number;
  name: string;
  value: string;
  is_ticket_issuance_enabled: boolean;
  description: string;
  created_at: Date;
  updated_at: Date;
};

export type Route = {
  path: string;
  component: any;
  private?: boolean;
  exact?: boolean;
  role?: number[];
};

export type ResponseData = {
  data?: any;
  message: string;
  success: boolean;
  status: number;
  response?: any;
  error?: any;
  results?: any;
};

export type Token = {
  access: string;
  refresh: string;
};

export type pxResponse = {
  ok?: boolean;
  statusCode?: number;
  data: ResponseData | null;
};

export type AgentListType = {
  address: string;
  category: string;
  code: string;
  contact_person: string;
  ["cr._limit"]: string;
  email: string;
  group: string;
  ["id/_cr_ no"]: string;
  name: string;
  phone: string;
};

export type AgentNewsListType = {
  id: number;
  news_title: string;
  news_type: string;
  news_description: string;
  featured_image: string;
  action_url: string;
  is_enabled: boolean;
  created_at: Date;
  show_on_banner: boolean;
  call_to_action_label: string;
};

export type AirlineListType = {
  id: number;
  airline: string;
  airline_value: string;
  issuance_status: boolean;
};

export type AgentPricingListDetails = {
  "Destinations / RBD": string;
  "Discount Capacity": string;
  "Sales Period": string;
  "Travel Period": string;
};

export type PassengersList = {
  title: string;
  person_name: string;
  surname: string;
  birthdate?: string;
  passenger_type_code?: string;
  pricing_detail?: {
    fare: ExtendedPrice;
    surcharges: ExtendedPrice;
    total_tax: ExtendedPrice;
    fees: ExtendedPrice;
    total_fare: ExtendedPrice;
    surcharges_xi?: ExtendedPrice | null;
    surcharges_xii?: ExtendedPrice | null;
  };
};

export type CityInfo = {
  code_context?: string;
  language?: string;
  location_code?: string;
  location_name?: string;
};

export type ArrivalDestinationInformation = {
  date_time: string;
  code_context?: string;
  language?: string;
  location_code?: string;
  location_name?: string;
  city_info?: CityInfo;
};

export type BookingClass = {
  cabin?: string;
};

export type FlightDetails = {
  arrival_date_time: string;
  arrival_location: string;
  arrival_short_code: string;
  departure_short_code: string;
  booking_class: string;
  departure_date_time: string;
  departure_location: string;
  flight: string;
  baggage_info?: AGBaggageInfo | null;
};

export enum MANDATORY_STAGE {
  ISSUANCE = "ISSUANCE",
  BOOKING = "BOOKING",
}

export interface PassengerField {
  public_id?: string | null;
  field_type?: PassengerFieldTypes;
  field_name: string;
  field_code: string;
  is_mandatory?: boolean;
  display: boolean;
  display_name: string;
  placeholder?: string | null;
  value?: PassengerFieldValue;
  options?: OptionValue[];
  max_entries?: number | null;
  deleted?: boolean | null;
  reference_url?: string | null;
}

export interface AdditionalPassengerField extends PassengerField {
  sabre_code?: string | null;
  allowed_values?: string[];
  category: string;
  maximum_field_length?: number | null;
  mandatory_stage?: MANDATORY_STAGE | null;
  savable?: boolean;
}

export type PassengerFieldValue =
  | string
  | number
  | Date
  | boolean
  | PhoneFieldValue
  | (string | number)[]
  | null;

export type RepeatablePassengerField = Record<string, PassengerFieldValue>;

export type GroupedPassengerField = Record<string, PassengerFieldValue>;

export type AdditionalFieldValues = Record<string, PassengerFieldValue>;

export interface AdditionalFieldErrors {
  [field_code: string]: string;
}

export type EditPassengerDataAdditionalField = Pick<
  AdditionalPassengerField,
  "field_code" | "value"
>;

export interface EditPassengerData {
  passenger_id: Pick<IPassenger, "public_id">["public_id"];
  additional_data: EditPassengerDataAdditionalField[];
}

export interface PassengerApiError {
  [key: string]: string | AdditionalFieldErrors | undefined;
  additional_data?: AdditionalFieldErrors;
}

export interface PassengerFormSection {
  section_name: string;
  is_group_fields: boolean;
  group_code: string | null;
  is_repeatable: boolean;
  fields: PassengerField[];
  min_occurrences: number;
  max_occurrences: number;
}

export interface IRepeatableRow {
  preferred?: boolean | null;
  public_id?: string | null;
}

export interface IRepeatableEmailRow extends IRepeatableRow {
  email: string | null;
  email_type: string | null;
  receive_e_ticket: boolean | null;
  receive_e_invoice: boolean | null;
}

export interface IRepeatablePhoneRow extends IRepeatableRow {
  phone_number: string | null;
  country_code: string | null;
  phone_number_type: string | null;
}

export interface IRepeatableDocumentRow extends IRepeatableRow {
  document_type: string | null;
  document_number: string | null;
  country_of_issuance: string | null;
  country_of_residence: string | null;
  nationality?: string | null;
  issue_date: Date | string | null;
  expiration_date: Date | string | null;
}

export interface IRepeatableFrequentFlyersRow extends IRepeatableRow {
  frequent_flyer_number: string | null;
  airline_code: string | null;
}

export interface IAirPreference {
  seat_type: string | null;
  seat_location: string | null;
  meal: string | null;
  meal_comment: string | null;
}

export interface EditPassengerApiErrors {
  passengers?: PassengerApiError[];
}

export interface EditPassengerRequest {
  passengers: EditPassengerData[];
}

export type AirSialTaxFeeDetails = {
  fare: string;
  surcharge: string;
  fees: string;
  taxes: string;
  total_amount: string;
};

export type AirArabiaTaxFeeDetails = {
  base_fare: string;
  fees: string;
  taxes: string;
  total_amount_actual: string;
  total_amount_equiv: string;
  actual_currency: string;
  equiv_currency: string;
};

export type PNRDetails = {
  passengers: Array<PassengersList>;
  flight_details: Array<FlightDetails>;
  baggage_info?: BaggageInfo | null;
  airsial_tax_fees_details?: AirSialTaxFeeDetails;
  air_arabia_tax_fees_details?: AirArabiaTaxFeeDetails;
  reference: string;
  booking_id: string;

  pnr_status: string;
  pnr_expiry: string;
  raw_pnr_expiry: string[] | null;

  total_amount: ExtendedPrice;
  total_amount_after_pricing: ExtendedPrice;
  base_fare: ExtendedPrice;
  gross_fare: ExtendedPrice;
  additional_charges: ExtendedPrice | null;
  service_tax: ExtendedPrice | null;
  esc_charges: ExtendedPrice | null;
  surcharges: ExtendedPrice | null;
  surcharges_xi?: ExtendedPrice | null;
  surcharges_xii?: ExtendedPrice | null;
  tax?: ExtendedPrice | null;
  fare_difference: ExtendedPrice;
  deal_promo_discount?: ExtendedPrice | null;

  discount_data: DealDiscount;
  total_tax?: ExtendedPrice;
  is_ticketed: boolean;
  fare_updated: boolean;
};

export type BookingPriceDetails = {
  price_detail_id: string;
  public_id: string;
  passengers: Array<PassengersList>;
  flight_details: Array<FlightDetails>;
  total_amount_after_pricing: ExtendedPrice;
  base_fare: ExtendedPrice;
  gross_fare: ExtendedPrice;
  additional_charges: ExtendedPrice | null;
  service_tax: ExtendedPrice | null;
  esc_charges: ExtendedPrice | null;
  total_amount: ExtendedPrice;
  fare_difference: ExtendedPrice;
  deal_promo_discount?: ExtendedPrice | null;
  pnr_status: PNR_STATUSES;
  is_ticketed: boolean;
  fare_updated: boolean;
  total_tax?: ExtendedPrice;
  discount_data: DealDiscount;
  surcharges_xi?: ExtendedPrice | null;
  surcharges_xii?: ExtendedPrice | null;
  surcharges?: ExtendedPrice | null;
  tax?: ExtendedPrice | null;
};

export type ProviderConfiguration = {
  key: string;
  sabre_pcc_code: string;
};

export type PNRDetailsV3 = {
  ag_equivalent_currency: string;
  reference: string;
  provider_configuration: ProviderConfiguration;
  booking_price_details: BookingPriceDetails[];
  pnr_expiry: string;
  booking_id: string;
  raw_pnr_expiry: string[] | null;
  pnr_status: string;
};

export type CalculatedFareV3 = {
  reference: string;
  booking_price_details: BookingPriceDetails[];
  total_amount_after_pricing: ExtendedPrice;
  fare_updated: boolean;
};

export type Sector = {
  id: number;
  created_at: string;
  last_updated_at: string;
  sector_name: string;
  city: string;
  ticket_issuance_emails: Array<string>;
  sales_emails: Array<string>;
};

export type SalesUserTypes = {
  id: number;
  created_at: Date;
  last_updated_at: Date;
  name: string;
  description: string;
};

export type AppSettingsTypes = {
  id: number;
  key: string;
  value: string;
};

export type Payment = {
  payment_id: string;
  charges: number;
  net_amount: number;
  financial_profile_code: string;
  payment_success_at: string | null;
  payment_receipt: string | null;
  amount: number;
  description: string;
  method: string;
  payment_status: string;
  invoice_id: string;
  invoice_code: number;
  receipt_status: string;
  expire_date: string;
  created_at: string;
  one_bill_payment_detail: {
    bill_id: string;
    bill_code: string;
    expiry_date: string;
  } | null;
  credit_card_payment_detail: {
    payment_id: string;
    payment_code: string;
    expiry_date: string;
    payment_url: string;
  } | null;
};

export type Bank = {
  id: number;
  bank_name: string;
  account: string;
};

export type BankAccount = {
  account: string;
  id: number;
  bank_name: string;
};

export type PaymentLog = {
  id: number;
  created_at: string;
  last_updated_at: string;
  comment: string;
  status: string;
  user: number;
  payment_history: number;
};

export type Fortnight = {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  created_at: string;
};

export type bookingDetailErrors = {
  contact_details?: { mobile_number: string } | string | null;
  travelers?: Traveler[] | null;
};

export type SalesUser = {
  email: string;
  full_name: string;
  id: number;
  username: string;
};

export * from "./utils";
